import { useState } from 'react'

import ToastService from 'services/toast.service'

import Modal from 'components/elements/Modal'
import PhatInput from 'components/formElements/PhatInput'
import PhatButton from 'components/formElements/PhatButton'
import SimpleSpinner from '../SimpleSpinner'

import styles from './textInputModal.module.scss'

type Props = {
  open: boolean
  header: string
  inputHeader: string
  onClose: () => void
  value?: string
  message?: string
  warningText?: string
} & (
  | { onSubmit: (value: string) => Promise<any>; onConfirm?: never }
  | { onSubmit?: never; onConfirm: (value: string) => void }
)
const TextInputModal = ({
  open,
  header,
  inputHeader,
  onClose,
  onSubmit,
  onConfirm,
  value = '',
  message,
  warningText,
}: Props) => {
  const [updatedValue, setUpdatedValue] = useState(value)
  const [showWarning, setShowWarning] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (updatedValue === value) {
      setShowWarning(true)
      return
    }

    if (onConfirm) return onConfirm(updatedValue)

    setLoading(true)
    return onSubmit(updatedValue)
      .then(() => handleClose())
      .catch(() => ToastService.create('Unable to edit value', '', 'error'))
      .finally(() => setLoading(false))
  }

  const handleClose = () => {
    setShowWarning(false)
    onClose()
  }

  return (
    <Modal open={open} onClose={handleClose} modalClassName={styles.modal}>
      <h2>{header}</h2>

      {message && <div className={styles.message}>{message}</div>}

      <form onSubmit={handleSubmit} className={styles.inputForm}>
        <PhatInput
          header={inputHeader}
          value={updatedValue}
          onChange={setUpdatedValue}
          focusOnMount
          className={styles.input}
          disabled={loading}
        />

        {showWarning && <div className={styles.error}>{warningText || 'Please enter a valid value.'}</div>}

        <div className={styles.controls}>
          <PhatButton color="green" type="submit" disabled={loading} className={styles.controlButton}>
            {!loading ? 'Confirm' : <SimpleSpinner size="xSmall" />}
          </PhatButton>
          <PhatButton
            color="red"
            type="button"
            disabled={loading}
            onClick={handleClose}
            className={styles.controlButton}>
            Cancel
          </PhatButton>
        </div>
      </form>
    </Modal>
  )
}

export default TextInputModal
